import { Question, Serializer, SurveyModel } from "survey-core";

export function registerSurveyCustomImageVideoProperties(): void {
  Serializer.addProperty('image', {
    name: 'requireVideoPlay',
    category: 'general',
    type: 'boolean',
    visibleIndex: 11,
    default: false,
  }); 
}

export function registerSurveyCustomImageVideoPropertiesTranslation(): void {
  // DO NOT LOAD in survey view-only mode, as it will throw an error because the creator chunk is not loaded
  // Dynamically import editor utils only when explicit registration is called
  import('./creator/Creator/utils-editor').then(({ registerPropertyTranslations }) => {
    registerPropertyTranslations('requireVideoPlay', {
        en: 'Require video completion before next page',
        de: 'Video-Wiedergabe vor nächster Seite erforderlich',
        hu: 'Videó lejátszása szükséges a következő oldal előtt',
        sl: 'Zahtevaj ogled videa pred naslednjo stranjo',
        hr: 'Zahtijevaj pregled videa prije sljedeće stranice',
      });
  }).catch(() => {
    // Gracefully handle view-only environments where creator chunk is not loaded
  });  
}

export function applyCustomImageVideo(survey: SurveyModel): void {
  // Tracks cleanup functions to prevent duplicate listeners on re-renders
  const cleanupMap = new WeakMap<Question, () => void>();

  survey.onAfterRenderQuestion.add((sender, options) => {
    const question = options.question;
    const isVideoRequired = question.getPropertyValue('requireVideoPlay') ?? question.requireVideoPlay;

    if (question.getType() === 'image' && isVideoRequired) {
      // If the question was already initialized, clean up old listeners first
      if (cleanupMap.has(question)) {
        cleanupMap.get(question)!();
      }

      const videoElement = options.htmlElement.querySelector('video') as HTMLVideoElement | null;

      if (videoElement) {
        updateNavigationState(sender, false);

        const checkProgress = () => {
          if (!videoElement.duration) return;
          const percentPlayed = (videoElement.currentTime / videoElement.duration) * 100;
          if (percentPlayed >= 90 || videoElement.ended) {
            updateNavigationState(sender, true);
            cleanup();
          }
        };

        const onEnded = () => updateNavigationState(sender, true);

        videoElement.addEventListener('timeupdate', checkProgress);
        videoElement.addEventListener('ended', onEnded);

        const cleanup = () => {
          videoElement.removeEventListener('timeupdate', checkProgress);
          videoElement.removeEventListener('ended', onEnded);
          cleanupMap.delete(question);
        };

        // Store cleanup function for next time
        cleanupMap.set(question, cleanup);

      } else {
        console.warn(`[Survey] Question "${question.name}" has requireVideoPlay enabled, but no <video> tag was found. Ensure "contentMode" is set to "video".`);
      }
    }
  });
}

function updateNavigationState(survey: SurveyModel, enable: boolean) {
  const nextAction = survey.navigationBar?.actions?.find(
    (action) => action.id === 'sv-nav-next'
  );

  if (nextAction) {
    nextAction.visible = enable;
    nextAction.enabled = enable;
  } else {
    // Fallback for edge cases
    survey.canNavigateForward = enable;
  }
}